import { useCallback, useEffect, useState } from "react";
import { Check, ChevronDown, ChevronUp, Loader2, Trash2, UserX, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/common";
import { api, errMsg, fmtDate, maskPhone } from "@/lib/api";
import { VEHICLE_LABEL } from "@/lib/boyCourier";
import { cn } from "@/lib/utils";
import { VehicleIcon } from "@/components/boy/courier/VehicleIcon";
import { CourierDetails } from "./CourierDetails";

const ST = {
  pendente: ["Aguardando aprovação", "bg-amber-500/15 text-amber-700 dark:text-amber-300"],
  aprovado: ["Aprovado", "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"],
  reprovado: ["Reprovado", "bg-red-500/15 text-red-700 dark:text-red-300"],
};

function CourierRow({ c, mgr, onStatus, onRemove }) {
  const [label, tone] = ST[c.status] || ST.pendente;
  const [open, setOpen] = useState(c.status === "pendente");
  return (
    <div className="card-surface space-y-3 p-4" data-testid={`boy-courier-${c.id}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted"><VehicleIcon id={c.vehicle} className="h-5 w-5" /></span>
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2"><p className="font-semibold">{c.name}</p><span className={cn("rounded-full px-2 py-0.5 text-xs font-semibold", tone)} data-testid={`boy-courier-status-${c.id}`}>{label}</span></div>
          <p className="text-xs text-muted-foreground">{VEHICLE_LABEL[c.vehicle] || "Moto"} · {c.city}/{c.state} · WhatsApp {maskPhone(c.whatsapp)} · cadastrado em {fmtDate(c.created_at)}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} data-testid={`boy-courier-toggle-${c.id}`}>
          {open ? <ChevronUp className="mr-1.5 h-4 w-4" /> : <ChevronDown className="mr-1.5 h-4 w-4" />}{open ? "Ocultar dados" : "Ver dados e documentos"}
        </Button>
        {c.status !== "aprovado" && <Button size="sm" onClick={() => onStatus(c, "aprovado")} data-testid={`boy-courier-approve-${c.id}`}><Check className="mr-1.5 h-4 w-4" />Aprovar</Button>}
        {c.status !== "reprovado" && <Button size="sm" variant="outline" onClick={() => onStatus(c, "reprovado")} data-testid={`boy-courier-reject-${c.id}`}><UserX className="mr-1.5 h-4 w-4" />Reprovar</Button>}
        {mgr && <Button size="icon" variant="ghost" onClick={() => onRemove(c)} title="Excluir cadastro" data-testid={`boy-courier-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
      </div>
      {open && <CourierDetails c={c} />}
    </div>
  );
}

export function CouriersTab({ mgr }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("todos");
  const load = useCallback(() => api.get("/boy/couriers").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
  }, [load]);
  const onStatus = async (c, status) => {
    try {
      await api.patch(`/boy/couriers/${c.id}`, { status });
      toast.success(status === "aprovado" ? `${c.name} aprovado: já recebe corridas` : `${c.name} reprovado`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const onRemove = async (c) => {
    if (!window.confirm(`Excluir o cadastro de ${c.name}?`)) return;
    try {
      await api.delete(`/boy/couriers/${c.id}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!rows) return <p className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></p>;
  const tabs = [["todos", "Todos"], ["pendente", "Aguardando"], ["aprovado", "Aprovados"], ["reprovado", "Reprovados"]];
  const list = rows.filter((c) => filter === "todos" || c.status === filter);
  return (
    <div className="space-y-4" data-testid="boy-couriers-tab">
      <div className="flex flex-wrap gap-2">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`boy-couriers-filter-${k}`}
            className={cn("rounded-full border px-3 py-1.5 text-sm transition-colors", filter === k ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent")}>
            {l}<span className="ml-1.5 font-mono text-xs opacity-70">{k === "todos" ? rows.length : rows.filter((c) => c.status === k).length}</span>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Só os entregadores aprovados recebem as corridas no WhatsApp, e apenas as que saem da cidade em que eles atuam. Ao aprovar ou reprovar, o entregador é avisado pelo WhatsApp. O link de cadastro fica em Boy Express → Configurações.</p>
      {!list.length ? <EmptyState icon={Users} title="Nenhum motoboy aqui" text="Os motoboys que se cadastrarem pelo link aparecem nesta lista." />
        : <div className="grid gap-3">{list.map((c) => <CourierRow key={c.id} c={c} mgr={mgr} onStatus={onStatus} onRemove={onRemove} />)}</div>}
    </div>
  );
}
